import { baseApi } from "../../../services/rtkApi/baseApi";
import { respectCardsPlayerPaths as P, respectCardsAdminPaths as A } from "./apiPaths";
import { handPaths } from "../types/handTypes";
import type {
  KeepCardsRequest,
  KeepCardsResponse,
  ResolveOfferRequest,
  ResolveOfferResponse,
} from "../types/handTypes";
import type {
  AdminCardRow,
  AdminScheduleDoc,
  AdminScheduleDrop,
  AdminSchedulePreview,
  AdminScheduleSummary,
  AdminUserCardInstance,
  AdminUserSummary,
  BlockUserResponse,
  CardDefinition,
  CardImpactDoc,
  CardStatus,
  CreateCardRequest,
  GenerateImagesResponse,
  GeneratePromptResponse,
  GetAdminCardResponse,
  GetAdminCardsResponse,
  GetCardCatalogResponse,
  GetHandSummaryResponse,
  GetLockedSummaryResponse,
  GetDropActivityFeedResponse,
  GetMyCardsResponse,
  GetMyEffectsResponse,
  GetPublicActiveEffectsResponse,
  GetRuntimeActionsResponse,
  GetRuntimeAotdStateResponse,
  GetRuntimeEffectsResponse,
  GetRuntimeEventsResponse,
  GetSurfaceCardFeedResponse,
  GetLobbyTodayResponse,
  GetTodayDropResponse,
  JoinLobbyResponse,
  LaunchDropResponse,
  CardRarity,
  LobbyParticipantModifiers,
  LobbyPullStatus,
  LobbySnapshot,
  LobbyTimingZone,
  PlayCardRequest,
  PlayCardResponse,
  PullCardResponse,
  RecommendCardResponse,
  SelectImageRequest,
  SimulateCatalogResponse,
  SimulatePullRequest,
  SimulatePullResponse,
  UpdateCardRequest,
} from "../types/cardTypes";

// ---- cards-drop-lobby phase 12 — dev sim-lobby DTOs -----------------------
// Mirror `buildSimDetail` in the backend `devSimulatorRoutes.ts`. The `sim`
// block carries the dev-only internals the public surfaces strip: the full
// (un-sanitized) timing curve on each `pullResult`, the per-slot effective
// chance after the realized multiplier, and the schedule seed/slots.
export interface SimLobbyDebugSlotChance {
  slotIndex: number;
  rarity: CardRarity;
  preTimingChance: number;
  effectiveChance: number;
}

export interface SimLobbyDebugPullResult {
  outcome: "card" | "nothing" | null;
  cardId: string | null;
  rarity: CardRarity | null;
  timingMultiplier: number | null;
  timingZone: LobbyTimingZone | null;
  jackpot: boolean;
  effectiveChance: number | null;
  elapsedMs: number | null;
}

export interface SimLobbyDebugParticipant {
  userId: string;
  username: string;
  arrivalRank: number;
  isLateJoiner: boolean;
  pullStatus: LobbyPullStatus;
  turnStartedAt: string | null;
  turnEndsAt: string | null;
  modifiers: LobbyParticipantModifiers;
  slotChances: SimLobbyDebugSlotChance[];
  pullResult: SimLobbyDebugPullResult | null;
}

export interface SimLobbyDebugSlot {
  slotIndex: number;
  rarity: CardRarity;
  baseHitChance: number;
  pinnedCardId: string | null;
}

export interface SimLobbyDetail {
  success: boolean;
  simLobbyId: string;
  date: string;
  snapshot: LobbySnapshot;
  sim: {
    seed: string | null;
    slots: SimLobbyDebugSlot[];
    participants: SimLobbyDebugParticipant[];
  };
  /** Present on a step response: the action that ran + its pull result (if any). */
  action?: string;
  pullResult?: SimLobbyDebugPullResult | null;
}

export interface SimLobbyParticipantSpec {
  username?: string;
  arrivalRank?: number;
  modifiers?: Partial<LobbyParticipantModifiers>;
}

export interface SimLobbySlotSpec {
  slotIndex?: number;
  rarity: CardRarity;
  baseHitChance: number;
  pinnedCardId?: string | null;
}

export interface CreateSimLobbyRequest {
  numUsers?: number;
  participants?: SimLobbyParticipantSpec[];
  gatheringWindowMs?: number;
  turnWindowMs?: number;
  slots?: SimLobbySlotSpec[];
}

export type SimLobbyStepAction =
  | "close-gathering"
  | "pull"
  | "auto-pull"
  | "add-late-joiner"
  | "block"
  | "unblock";

export interface StepSimLobbyRequest {
  id: string;
  action: SimLobbyStepAction;
  /** pull: server-measured elapsed within the turn window (drives the curve). */
  elapsedMs?: number;
  /** pull: bypass the elapsed→multiplier curve with a direct multiplier. */
  timingMultiplier?: number;
  /** pull (target a late joiner) / add-late-joiner / block / unblock. */
  username?: string;
}

export const respectCardsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Phase 2 cards-launch: the public `/cards/catalog` endpoint was
     * removed. Use `getLockedSummary` for the locked silhouette grid
     * and `getMyCards` for the user's owned tiles. Admin tooling hits
     * `/api/v2/respect/admin/cards/catalog` directly.
     */
    getLockedSummary: builder.query<GetLockedSummaryResponse, void>({
      query: () => P.lockedSummary,
      providesTags: ["Cards"],
    }),
    /**
     * Phase 2 cards-launch: narrow read kept open for the Agent
     * Influence game UI which needs role-card metadata only. Returns
     * exactly the `agent_influence`-typed cards from the active
     * catalog.
     */
    getAgentInfluenceRoleCards: builder.query<GetCardCatalogResponse, void>({
      query: () => P.agentInfluenceRoles,
    }),
    getTodayDrop: builder.query<GetTodayDropResponse, void>({
      query: () => P.dropToday,
      providesTags: ["Cards"],
    }),
    pullCard: builder.mutation<PullCardResponse, void>({
      query: () => ({
        url: P.dropPull,
        method: "POST",
      }),
      // A pull adds a card to the hand — refresh the global hand badge too.
      invalidatesTags: ["Cards", "HandSummary"],
    }),
    getMyCards: builder.query<GetMyCardsResponse, void>({
      query: () => P.myCards,
      providesTags: ["Cards"],
    }),
    /**
     * cards-drop-lobby — join today's gathering lobby (creating it if the pool
     * just opened). Returns the viewer's snapshot, flagging late-joiner status.
     * Blocked users get a 403.
     */
    joinLobby: builder.mutation<JoinLobbyResponse, void>({
      query: () => ({
        url: P.dropLobbyJoin,
        method: "POST",
      }),
      invalidatesTags: ["Cards"],
    }),
    /**
     * cards-drop-lobby — viewer-aware snapshot of today's lobby. The backend
     * returns 204 (no body → `null` data) when no lobby exists yet today.
     */
    getLobbyToday: builder.query<GetLobbyTodayResponse | null, void>({
      query: () => P.dropLobbyToday,
      providesTags: ["Cards"],
    }),
    /**
     * cards-drop-lobby — public newest-first feed of recent pulls (who pulled
     * what, the rarity, and the chance they had). Backs the homepage widget;
     * polled (not SSE) to stay cheap. Optional auth adds the puller `userEmoji`.
     */
    getDropActivityFeed: builder.query<
      GetDropActivityFeedResponse,
      { limit?: number; before?: string } | void
    >({
      query: (arg) => ({
        url: P.dropActivityFeed,
        params: {
          ...(arg?.limit ? { limit: arg.limit } : {}),
          ...(arg?.before ? { before: arg.before } : {}),
        },
      }),
      providesTags: ["Cards"],
    }),
    getMyEffects: builder.query<GetMyEffectsResponse, void>({
      query: () => P.myEffects,
      providesTags: ["Cards"],
    }),
    /**
     * Cheap hand badge summary (handCount / playableNowCount / nextExpiryAt /
     * activeEffectCount). The global chrome (P4) polls this; here it feeds the
     * `/cards` section counts without a full /my-cards fetch.
     */
    getHandSummary: builder.query<GetHandSummaryResponse, void>({
      query: () => P.handSummary,
      // Its own tag so a play / pull / reveal-close can invalidate the badge
      // in isolation; keep `Cards` so admin card-assign/remove refreshes it too.
      providesTags: ["HandSummary", "Cards"],
    }),
    /**
     * Recent distinct AOTD creators — backs the Gridlock target picker.
     * Mirrors `GET /api/art-of-the-day/recent-creators?limit=N` from
     * `n450s_backend/modules/aotd/routes/info.ts`.
     */
    getRecentAotdCreators: builder.query<
      { creators: Array<{ userId: string; username: string; lastAotdAt: string }> },
      { limit?: number } | void
    >({
      query: (arg) => ({
        url: "/api/art-of-the-day/recent-creators",
        params: { limit: (arg && arg.limit) || 5 },
      }),
    }),
    /**
     * Player-facing recipient search — backs the Tribute (respect gift) target
     * picker. Hits `GET /api/cards/users/search?query=` (auth + backend:read);
     * excludes the requester and returns a minimal {userId, username, userEmoji}.
     */
    searchRecipients: builder.query<
      { success: boolean; users: Array<{ userId: string; username: string; userEmoji: string | null }> },
      { query?: string; limit?: number } | void
    >({
      query: (arg) => ({
        url: P.recipientSearch,
        params: {
          query: (arg && arg.query) || "",
          limit: (arg && arg.limit) || 10,
        },
      }),
    }),
    /** Phase 9: surface-agnostic banner read for AOTD/race/comment scopes. */
    getPublicActiveEffects: builder.query<
      GetPublicActiveEffectsResponse,
      { scopes?: string[] } | void
    >({
      query: (arg) => {
        const scopes = arg && Array.isArray(arg.scopes) ? arg.scopes : null;
        const params = scopes && scopes.length > 0 ? { scope: scopes.join(",") } : undefined;
        return {
          url: P.publicActiveEffects,
          ...(params ? { params } : {}),
        };
      },
      providesTags: ["Cards"],
    }),
    getSurfaceCardFeed: builder.query<
      GetSurfaceCardFeedResponse,
      { surface: string; entityId?: string | null }
    >({
      query: ({ surface, entityId }) => ({
        url: P.contextFeed(surface),
        params: entityId ? { entityId } : undefined,
      }),
      providesTags: ["Cards"],
    }),
    playCard: builder.mutation<PlayCardResponse, PlayCardRequest>({
      query: ({ cardInstanceId, ...body }) => ({
        url: P.play(cardInstanceId),
        method: "POST",
        body,
      }),
      // Playing a card leaves the hand and (often) starts an effect — refresh
      // the global hand badge in the same invalidation cycle. Second Guess
      // changes today's quiz board (one more attempt) and Reclamation frees
      // used emojis, so those reads refresh too.
      invalidatesTags: ["Cards", "HandSummary", "AotdQuiz", "AotdEmojis"],
    }),
    /**
     * Keep an offered card (throwing `discardInstanceId` when the hand is
     * full) or throw it away. Answers with the next queued offer, if any.
     */
    resolveCardOffer: builder.mutation<
      ResolveOfferResponse,
      { offerId: string } & ResolveOfferRequest
    >({
      query: ({ offerId, ...body }) => ({
        url: handPaths.resolveOffer(offerId),
        method: "POST",
        body,
      }),
      invalidatesTags: ["Cards", "HandSummary"],
    }),
    /** The keep-4 picker: keep exactly hand.size in_hand cards, return the rest. */
    keepCards: builder.mutation<KeepCardsResponse, KeepCardsRequest>({
      query: (body) => ({
        url: handPaths.keep,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Cards", "HandSummary"],
    }),

    getAdminCards: builder.query<GetAdminCardsResponse, void>({
      query: () => A.cards,
      providesTags: ["AdminCards", "Cards"],
    }),
    getAdminCard: builder.query<GetAdminCardResponse, string>({
      query: (slug) => A.card(slug),
      providesTags: ["AdminCards", "Cards"],
    }),
    getAdminRuntimeEvents: builder.query<
      GetRuntimeEventsResponse,
      { limit?: number; eventType?: string; streamId?: string; actorUsername?: string } | void
    >({
      query: (params) => ({
        url: A.runtimeEvents,
        params: params ?? undefined,
      }),
      providesTags: ["AdminCards"],
    }),
    getAdminRuntimeEffects: builder.query<
      GetRuntimeEffectsResponse,
      {
        limit?: number;
        status?: string;
        effectType?: string;
        scope?: string;
        ownerUsername?: string;
        sourceCardSlug?: string;
      } | void
    >({
      query: (params) => ({
        url: A.runtimeEffects,
        params: params ?? undefined,
      }),
      providesTags: ["AdminCards", "Cards"],
    }),
    getAdminRuntimeActions: builder.query<
      GetRuntimeActionsResponse,
      { limit?: number; status?: string; actionType?: string } | void
    >({
      query: (params) => ({
        url: A.runtimeActions,
        params: params ?? undefined,
      }),
      providesTags: ["AdminCards"],
    }),
    getAdminRuntimeAotdState: builder.query<GetRuntimeAotdStateResponse, void>({
      query: () => A.runtimeAotdToday,
      providesTags: ["AdminCards", "Cards"],
    }),
    createAdminCard: builder.mutation<
      { success: boolean; card: CardDefinition },
      CreateCardRequest
    >({
      query: (body) => ({
        url: A.cards,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminCards", "Cards"],
    }),
    updateAdminCard: builder.mutation<
      { success: boolean; card: CardDefinition },
      { slug: string; body: UpdateCardRequest }
    >({
      query: ({ slug, body }) => ({
        url: A.card(slug),
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AdminCards", "Cards"],
    }),
    deleteAdminCard: builder.mutation<
      { success: boolean; message: string; card: CardDefinition },
      string
    >({
      query: (slug) => ({
        url: A.card(slug),
        method: "DELETE",
      }),
      invalidatesTags: ["AdminCards", "Cards"],
    }),
    toggleAdminCard: builder.mutation<
      { success: boolean; card: AdminCardRow },
      string
    >({
      query: (slug) => ({
        url: A.cardToggle(slug),
        method: "POST",
      }),
      invalidatesTags: ["AdminCards", "Cards"],
    }),
    generateCardArtPrompt: builder.mutation<
      GeneratePromptResponse,
      { slug: string; useAi?: boolean }
    >({
      query: ({ slug, useAi }) => ({
        url: A.cardGeneratePrompt(slug),
        method: "POST",
        body: { useAi: useAi !== false },
      }),
    }),
    generateCardArtImages: builder.mutation<
      GenerateImagesResponse,
      { slug: string; prompt: string; count?: 1 | 2 }
    >({
      query: ({ slug, prompt, count }) => ({
        url: A.cardGenerateImages(slug),
        method: "POST",
        body: { prompt, ...(count === 1 ? { count: 1 } : {}) },
      }),
    }),
    selectCardArtImage: builder.mutation<
      { success: boolean; imageUrl: string; card: CardDefinition | null },
      { slug: string } & (
        | SelectImageRequest
        | { imageBase64: string; imagePrompt?: string }
      )
    >({
      query: ({ slug, ...body }) => ({
        url: A.cardSelectImage(slug),
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminCards", "Cards"],
    }),
    recommendCard: builder.mutation<RecommendCardResponse, { idea?: string }>({
      query: (body) => ({
        url: A.recommend,
        method: "POST",
        body,
      }),
    }),

    // Phase 2b cards-launch — schedule + impact-map admin endpoints.
    getAdminCardCatalog: builder.query<{ success: boolean; cards: CardDefinition[] }, void>({
      query: () => A.catalog,
      providesTags: ["AdminCards", "Cards"],
    }),
    getAdminCardSchedule: builder.query<
      {
        success: boolean;
        from: string;
        to: string;
        schedules: AdminScheduleSummary[];
      },
      void
    >({
      query: () => A.schedule,
      providesTags: ["AdminCardSchedule"],
    }),
    getAdminCardScheduleDay: builder.query<
      {
        success: boolean;
        schedule: AdminScheduleDoc;
        drop: AdminScheduleDrop | null;
      },
      string
    >({
      query: (date) => A.scheduleDay(date),
      providesTags: (_r, _e, date) => [{ type: "AdminCardSchedule", id: date }],
    }),
    regenerateAdminCardSchedule: builder.mutation<
      { success: boolean; schedule: AdminScheduleDoc },
      { date: string; overrides?: Record<string, unknown> }
    >({
      query: ({ date, overrides }) => ({
        url: A.scheduleRegenerate(date),
        method: "POST",
        body: overrides ?? {},
      }),
      invalidatesTags: (_r, _e, { date }) => [
        "AdminCardSchedule",
        { type: "AdminCardSchedule", id: date },
      ],
    }),
    previewAdminCardSchedule: builder.mutation<
      { success: boolean; preview: AdminSchedulePreview },
      { date: string; overrides?: Record<string, unknown> }
    >({
      query: ({ date, overrides }) => ({
        url: A.schedulePreview(date),
        method: "POST",
        body: overrides ?? {},
      }),
    }),
    patchAdminCardSchedule: builder.mutation<
      { success: boolean; schedule: AdminScheduleDoc },
      { date: string; patch: Record<string, unknown> }
    >({
      query: ({ date, patch }) => ({
        url: A.scheduleDay(date),
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_r, _e, { date }) => [
        "AdminCardSchedule",
        { type: "AdminCardSchedule", id: date },
        // Today's-drop view on DropControlTab also reads opensAt/closesAt;
        // include the broad Cards tag so it refreshes after a schedule patch.
        "Cards",
      ],
    }),
    ensureAdminCardScheduleHorizon: builder.mutation<
      { success: boolean; horizon: number; created?: number; ensured?: number },
      { daysAhead?: number }
    >({
      query: (body) => ({
        url: A.scheduleHorizon,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminCardSchedule"],
    }),

    // cards-drop-lobby phase 10 — drop control plane (phase-5 admin routes).
    /** Open today's drop immediately (creates/opens the lobby + fires the open notification). */
    launchDropNow: builder.mutation<LaunchDropResponse, void>({
      query: () => ({
        url: A.dropLaunch,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Cards", "AdminCardSchedule"],
    }),
    /** Bar a user from today's pull (rejected at join AND pull). */
    blockDropUser: builder.mutation<
      BlockUserResponse,
      { username: string; reason?: string }
    >({
      query: ({ username, reason }) => ({
        url: A.dropBlock,
        method: "POST",
        body: { username, ...(reason ? { reason } : {}) },
      }),
      invalidatesTags: ["Cards"],
    }),
    /** Lift a user's block for today. */
    unblockDropUser: builder.mutation<BlockUserResponse, { username: string }>({
      query: ({ username }) => ({
        url: A.dropUnblock,
        method: "POST",
        body: { username },
      }),
      invalidatesTags: ["Cards"],
    }),
    /** Wipe a day's lobby + drop so the day can re-run cleanly (admin). */
    resetDropDay: builder.mutation<
      {
        success: boolean;
        date: string;
        deletedLobby: boolean;
        deletedDrop: boolean;
        scheduleStatus: string;
      },
      { date?: string } | void
    >({
      query: (body) => ({
        url: A.dropReset,
        method: "POST",
        body: body ?? {},
      }),
      invalidatesTags: ["Cards", "AdminCardSchedule"],
    }),
    getAdminImpactMap: builder.query<
      { success: boolean; knownEffectTypes: string[]; impacts: Record<string, CardImpactDoc> },
      void
    >({
      query: () => A.impactMap,
    }),
    getAdminImpactMapForCard: builder.query<
      { success: boolean; slug: string; effectType: string; impact: CardImpactDoc },
      string
    >({
      query: (slug) => A.impactMapCard(slug),
    }),
    getAdminImpactCoverage: builder.query<
      {
        success: boolean;
        totalReferencedTypes: number;
        documented: number;
        missing: string[];
      },
      void
    >({
      query: () => A.impactMapCoverage,
    }),

    // Admin: assign / remove cards from users
    searchAdminUsers: builder.query<
      { success: boolean; users: AdminUserSummary[] },
      { query?: string; limit?: number } | void
    >({
      query: (arg) => ({
        url: A.usersSearch,
        params: {
          ...(arg?.query ? { query: arg.query } : {}),
          ...(arg?.limit ? { limit: arg.limit } : {}),
        },
      }),
    }),
    getAdminUserCards: builder.query<
      {
        success: boolean;
        user: { _id: string; username: string };
        cards: AdminUserCardInstance[];
      },
      string
    >({
      query: (userId) => A.userCards(userId),
      providesTags: (_r, _e, userId) => [
        { type: "AdminUserCards", id: userId },
        "AdminUserCards",
      ],
    }),
    assignAdminCardToUser: builder.mutation<
      {
        success: boolean;
        message: string;
        instance: AdminUserCardInstance;
      },
      { userId: string; cardId: string; source?: "admin" | "gift" | "event" | "milestone" }
    >({
      query: ({ userId, cardId, source }) => ({
        url: A.userCards(userId),
        method: "POST",
        body: { cardId, ...(source ? { source } : {}) },
      }),
      invalidatesTags: (_r, _e, { userId }) => [
        { type: "AdminUserCards", id: userId },
        "AdminUserCards",
        "AdminCards",
        "Cards",
      ],
    }),
    removeAdminCardInstance: builder.mutation<
      {
        success: boolean;
        message: string;
        removed: { _id: string; userId: string; cardId: string; status: CardStatus };
      },
      { instanceId: string; userId: string }
    >({
      query: ({ instanceId }) => ({
        url: A.cardInstance(instanceId),
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { userId }) => [
        { type: "AdminUserCards", id: userId },
        "AdminUserCards",
        "AdminCards",
        "Cards",
      ],
    }),

    /**
     * Dev-only — backend rejects with 404 when NODE_ENV=production.
     * Returns a card from the catalog without persisting a CardInstance,
     * so the admin can re-trigger the reveal animation indefinitely.
     */
    simulatePull: builder.mutation<SimulatePullResponse, SimulatePullRequest | void>({
      query: (body) => ({
        url: A.devSimulatePull,
        method: "POST",
        body: body ?? {},
      }),
    }),
    getSimulateCatalog: builder.query<SimulateCatalogResponse, void>({
      query: () => A.devSimulateCatalog,
    }),

    /**
     * cards-drop-lobby phase 11/12 — dev sim-lobby harness. Each route 404s
     * when NODE_ENV=production. The sim panel drives the REAL lobby engine
     * (close/pull/auto-pull/late-join/block) step-by-step with injected timing,
     * then renders the actual lobby UI against the returned snapshot.
     */
    createSimLobby: builder.mutation<SimLobbyDetail, CreateSimLobbyRequest | void>({
      query: (body) => ({
        url: A.devSimLobbyCreate,
        method: "POST",
        body: body ?? {},
      }),
    }),
    stepSimLobby: builder.mutation<SimLobbyDetail, StepSimLobbyRequest>({
      query: ({ id, ...body }) => ({
        url: A.devSimLobbyStep(id),
        method: "POST",
        body,
      }),
    }),
    getSimLobby: builder.query<SimLobbyDetail, string>({
      query: (id) => A.devSimLobby(id),
    }),
    /** Tear down the sim lobby + its schedule/drop docs and any fake instances. */
    resetSimLobby: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: A.devSimLobbyReset(id),
        method: "POST",
        body: {},
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetLockedSummaryQuery,
  useGetAgentInfluenceRoleCardsQuery,
  useGetTodayDropQuery,
  useGetRecentAotdCreatorsQuery,
  useSearchRecipientsQuery,
  usePullCardMutation,
  useJoinLobbyMutation,
  useGetLobbyTodayQuery,
  useGetDropActivityFeedQuery,
  useGetMyCardsQuery,
  useGetMyEffectsQuery,
  useGetHandSummaryQuery,
  useGetPublicActiveEffectsQuery,
  useGetSurfaceCardFeedQuery,
  usePlayCardMutation,
  useResolveCardOfferMutation,
  useKeepCardsMutation,
  useGetAdminCardsQuery,
  useGetAdminCardQuery,
  useGetAdminRuntimeEventsQuery,
  useGetAdminRuntimeEffectsQuery,
  useGetAdminRuntimeActionsQuery,
  useGetAdminRuntimeAotdStateQuery,
  useCreateAdminCardMutation,
  useUpdateAdminCardMutation,
  useDeleteAdminCardMutation,
  useToggleAdminCardMutation,
  useGenerateCardArtPromptMutation,
  useGenerateCardArtImagesMutation,
  useSelectCardArtImageMutation,
  useRecommendCardMutation,
  useGetAdminCardCatalogQuery,
  useGetAdminCardScheduleQuery,
  useGetAdminCardScheduleDayQuery,
  useRegenerateAdminCardScheduleMutation,
  usePreviewAdminCardScheduleMutation,
  usePatchAdminCardScheduleMutation,
  useEnsureAdminCardScheduleHorizonMutation,
  useLaunchDropNowMutation,
  useBlockDropUserMutation,
  useUnblockDropUserMutation,
  useResetDropDayMutation,
  useGetAdminImpactMapQuery,
  useGetAdminImpactMapForCardQuery,
  useGetAdminImpactCoverageQuery,
  useSearchAdminUsersQuery,
  useLazySearchAdminUsersQuery,
  useGetAdminUserCardsQuery,
  useLazyGetAdminUserCardsQuery,
  useAssignAdminCardToUserMutation,
  useRemoveAdminCardInstanceMutation,
  useSimulatePullMutation,
  useGetSimulateCatalogQuery,
  useCreateSimLobbyMutation,
  useStepSimLobbyMutation,
  useGetSimLobbyQuery,
  useLazyGetSimLobbyQuery,
  useResetSimLobbyMutation,
} = respectCardsApi;

export * from "../types/cardTypes";
